import { useMemo, useState, useEffect } from 'react';

// utils
import {
  constructQueryParamName,
  convertCategoriesToSelectTreeOptions,
  parseSelectFilterOptions,
} from '../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_LONG, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import getCountryCodes from '../../translations/countryCodes';
import { getCountryDialect } from '../../util/constants';
import { getAvailableDialectCountries } from '../../util/api';

// component imports
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';

// Module-level cache to ensure active dialect countries are fetched once platform-wide
let cachedActiveDialectCountries = null;
let activeDialectCountriesPromise = null;

const fetchActiveDialectCountries = () => {
  if (cachedActiveDialectCountries) {
    return Promise.resolve(cachedActiveDialectCountries);
  }
  if (!activeDialectCountriesPromise) {
    activeDialectCountriesPromise = getAvailableDialectCountries()
      .then(res => {
        const list = res?.data?.data || res?.data;
        if (Array.isArray(list) && list.length > 0) {
          cachedActiveDialectCountries = list;
        }
        return cachedActiveDialectCountries;
      })
      .catch(err => {
        console.warn('Failed to fetch available dialect countries, using fallback list:', err?.message || err);
        return null;
      })
      .finally(() => {
        activeDialectCountriesPromise = null;
      });
  }
  return activeDialectCountriesPromise;
};

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    intl,
    listings = [],
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-');

  // Track platform-wide active dialect country codes to ensure options remain complete during filtering
  const [activeDialectCountryCodes, setActiveDialectCountryCodes] = useState(
    cachedActiveDialectCountries
  );

  useEffect(() => {
    let isMounted = true;
    if (key === 'dialects' && !cachedActiveDialectCountries) {
      fetchActiveDialectCountries().then(list => {
        if (isMounted && Array.isArray(list) && list.length > 0) {
          setActiveDialectCountryCodes(list);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [key]);

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={convertCategoriesToSelectTreeOptions(listingCategories)}
          isNestedEnum={isNestedEnum}
          {...rest}
        />
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' })}
          queryParamNames={[paramNames]}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          {...rest}
        />
      );
    }
    case 'seats': {
      return (
        <SeatsFilter
          id={componentId}
          name={name}
          label={intl.formatMessage({ id: 'FilterComponent.seatsLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    }
  }

  // Pre-compute selected values from urlQueryParams to avoid repeating parsing on render
  const selectedValues = useMemo(() => {
    const scope = config.scope || 'public';
    const queryParamName = constructQueryParamName(key, scope);
    const selectedValueRaw = urlQueryParams[queryParamName];
    return selectedValueRaw ? parseSelectFilterOptions(selectedValueRaw) : [];
  }, [urlQueryParams, key, config.scope]);

  // Pre-compute present values in a Set for O(1) lookups during filtering
  const presentValues = useMemo(() => {
    const values = new Set();
    listings.forEach(listing => {
      const val = listing?.attributes?.publicData?.[key];
      const authorVal = listing?.author?.attributes?.profile?.publicData?.[key];
      [val, authorVal].forEach(v => {
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) {
          v.forEach(item => values.add(String(item)));
        } else {
          values.add(String(v));
        }
      });
      if (key === 'dialects') {
        const pd = listing?.attributes?.publicData || {};
        const authorPd = listing?.author?.attributes?.profile?.publicData || {};
        const c1 = pd.dialectCountry || pd.countryOfOrigin || pd.country;
        const c2 = authorPd.dialectCountry || authorPd.countryOfOrigin || authorPd.country;
        [c1, c2].forEach(c => {
          if (c) {
            values.add(String(c).toUpperCase());
            values.add(String(c).toLowerCase());
          }
        });
      }
    });
    return values;
  }, [listings, key]);

  const isOptionPresent = optionValue => {
    const valStr = String(optionValue);
    if (presentValues.has(valStr) || presentValues.has(valStr.toLowerCase()) || presentValues.has(valStr.toUpperCase())) {
      return true;
    }
    if (key === 'dialects') {
      const cd = getCountryDialect(optionValue);
      if (cd) {
        if (
          presentValues.has(cd.countryCode) ||
          presentValues.has(cd.countryCode.toLowerCase()) ||
          presentValues.has(cd.dialectName.toLowerCase())
        ) {
          return true;
        }
      }
    }
    return false;
  };

  // Helper to check if an option is currently selected in URL or state,
  // matching either canonical country code (e.g. 'EG') or legacy alias ('egyptian').
  const isOptionSelected = optValue => {
    if (selectedValues.includes(optValue)) return true;
    if (key === 'dialects') {
      const cdOpt = getCountryDialect(optValue);
      const optCode = cdOpt ? cdOpt.countryCode : String(optValue).toUpperCase();
      return selectedValues.some(sv => {
        const cdSv = getCountryDialect(sv);
        const svCode = cdSv ? cdSv.countryCode : String(sv).toUpperCase();
        return optCode === svCode;
      });
    }
    return false;
  };

  // Check if a dialect option is supported by active tutors platform-wide or in current search listings
  const isDialectOptionActive = optValue => {
    if (isOptionSelected(optValue)) {
      return true;
    }
    const cd = getCountryDialect(optValue);
    const code = cd ? cd.countryCode : String(optValue).toUpperCase();

    if (activeDialectCountryCodes && activeDialectCountryCodes.length > 0) {
      if (activeDialectCountryCodes.includes(code)) return true;
      if (code === 'MSA' && (activeDialectCountryCodes.includes('MSA') || presentValues.has('msa'))) {
        return true;
      }
    }
    return isOptionPresent(optValue);
  };

  // Deduplicate dialect options by canonical country dialect mapping.
  // This removes duplicate compatibility aliases (e.g. 'levantine', 'gulf', 'egyptian')
  // which duplicate primary country codes ('LB', 'AE', 'EG') with identical display labels.
  const deduplicatedEnumOptions = useMemo(() => {
    const rawOptions = config.enumOptions || [];
    if (key !== 'dialects') {
      return rawOptions;
    }
    const seenCodes = new Set();
    const result = [];
    rawOptions.forEach(opt => {
      const cd = getCountryDialect(opt.option);
      const canonicalCode = cd ? cd.countryCode : String(opt.option).toUpperCase();
      if (!seenCodes.has(canonicalCode)) {
        seenCodes.add(canonicalCode);
        result.push(opt);
      }
    });
    return result;
  }, [config.enumOptions, key]);

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, filterConfig = {} } = config;
      const { label, labelId, filterType } = filterConfig;
      // Try explicit labelId, then convention-based id, then fallback to raw label
      const labelIdByConvention = `ListingField.${key}.label`;
      const localizedLabel = labelId
        ? intl.formatMessage({ id: labelId })
        : intl.messages[labelIdByConvention]
        ? intl.formatMessage({ id: labelIdByConvention })
        : label;

      const shouldFilterByPresent =
        (key === 'country' || key === 'countryOfOrigin' || key === 'dialects') &&
        (presentValues.size > 0 || (activeDialectCountryCodes && activeDialectCountryCodes.length > 0));

      const filteredOptions = shouldFilterByPresent
        ? deduplicatedEnumOptions.filter(opt => {
            if (key === 'dialects') {
              return isDialectOptionActive(opt.option);
            }
            if (selectedValues.includes(opt.option)) {
              return true;
            }
            return isOptionPresent(opt.option);
          })
        : deduplicatedEnumOptions;

      const optionsToUse =
        filteredOptions.length > 0 ? filteredOptions : deduplicatedEnumOptions;

      const isArabic = (intl?.locale || '').toLowerCase().startsWith('ar');

      const localizedOptions = optionsToUse.map(opt => {
        const optionIdByConvention = `ListingField.${key}.option.${opt.option}`;
        let resolvedLabel = null;
        if (key === 'country' || key === 'countryOfOrigin') {
          const countries = getCountryCodes(intl.locale);
          const countryObj = countries.find(c => c.code === opt.option);
          if (countryObj) {
            resolvedLabel = countryObj.name;
          }
        } else if (key === 'dialects') {
          const cd = getCountryDialect(opt.option);
          if (cd) {
            // User-facing dialect labels use full country names only (never Levantine, Gulf, etc., or parenthetical combinations)
            resolvedLabel = isArabic
              ? (cd.arabicDialectName || cd.arabicCountryName)
              : (cd.dialectName || cd.countryName);
          }
        }
        if (!resolvedLabel) {
          resolvedLabel = opt.labelId
            ? intl.formatMessage({ id: opt.labelId })
            : intl.messages[optionIdByConvention]
            ? intl.formatMessage({ id: optionIdByConvention })
            : opt.label;
        }
        return { ...opt, label: resolvedLabel };
      });
      const queryParamNames = [constructQueryParamName(key, scope)];
      const rawInitVals = initialValues(queryParamNames, liveEdit);
      let normalizedInitVals = rawInitVals;
      if (key === 'teachingCategories' || key === 'teaching_categories') {
        const canonicalKey = constructQueryParamName(key, scope);
        const altKey =
          key === 'teachingCategories' ? 'pub_teaching_categories' : 'pub_teachingCategories';
        if (!normalizedInitVals?.[canonicalKey]) {
          const altInitVals = initialValues([altKey], liveEdit);
          if (altInitVals?.[altKey]) {
            normalizedInitVals = {
              ...normalizedInitVals,
              [canonicalKey]: altInitVals[altKey],
            };
          }
        }
      }
      if (key === 'dialects' && rawInitVals) {
        const qParamName = constructQueryParamName(key, scope);
        const raw = rawInitVals[qParamName];
        if (raw) {
          const prefixMatch = String(raw).match(/^(has_any|has_all):/);
          const prefix = prefixMatch ? prefixMatch[0] : '';
          const parsed = parseSelectFilterOptions(raw);
          const canonicalSet = new Set();
          parsed.forEach(val => {
            const cd = getCountryDialect(val);
            if (cd) {
              canonicalSet.add(cd.countryCode);
            } else {
              canonicalSet.add(val);
            }
          });
          normalizedInitVals = {
            ...rawInitVals,
            [qParamName]: `${prefix}${Array.from(canonicalSet).join(',')}`,
          };
        }
      }

      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={normalizedInitVals}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedOptions}
          isNestedEnum={false}
          showRatingStar={key === 'tutorMinRating'}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={normalizedInitVals}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedOptions}
          schemaType={schemaType}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, filterConfig = {} } = config;
      const { label, labelId, searchMode } = filterConfig;
      const labelIdByConvention = `ListingField.${key}.label`;
      const localizedLabel = labelId
        ? intl.formatMessage({ id: labelId })
        : intl.messages[labelIdByConvention]
        ? intl.formatMessage({ id: labelIdByConvention })
        : label;

      const shouldFilterByPresent =
        (key === 'country' || key === 'countryOfOrigin' || key === 'dialects') &&
        (presentValues.size > 0 || (activeDialectCountryCodes && activeDialectCountryCodes.length > 0));

      const filteredOptions = shouldFilterByPresent
        ? deduplicatedEnumOptions.filter(opt => {
            if (key === 'dialects') {
              return isDialectOptionActive(opt.option);
            }
            if (selectedValues.includes(opt.option)) {
              return true;
            }
            return isOptionPresent(opt.option);
          })
        : deduplicatedEnumOptions;

      const optionsToUse =
        filteredOptions.length > 0 ? filteredOptions : deduplicatedEnumOptions;

      const isArabic = (intl?.locale || '').toLowerCase().startsWith('ar');

      const localizedOptions = optionsToUse.map(opt => {
        const optionIdByConvention = `ListingField.${key}.option.${opt.option}`;
        let resolvedLabel = null;
        if (key === 'country' || key === 'countryOfOrigin') {
          const countries = getCountryCodes(intl.locale);
          const countryObj = countries.find(c => c.code === opt.option);
          if (countryObj) {
            resolvedLabel = countryObj.name;
          }
        } else if (key === 'dialects') {
          const cd = getCountryDialect(opt.option);
          if (cd) {
            // User-facing dialect labels use full country names only (never Levantine, Gulf, etc., or parenthetical combinations)
            resolvedLabel = isArabic
              ? (cd.arabicDialectName || cd.arabicCountryName)
              : (cd.dialectName || cd.countryName);
          }
        }
        if (!resolvedLabel) {
          resolvedLabel = opt.labelId
            ? intl.formatMessage({ id: opt.labelId })
            : intl.messages[optionIdByConvention]
            ? intl.formatMessage({ id: optionIdByConvention })
            : opt.label;
        }
        return { ...opt, label: resolvedLabel };
      });
      const queryParamNames = [constructQueryParamName(key, scope)];
      const rawInitVals = initialValues(queryParamNames, liveEdit);
      let normalizedInitVals = rawInitVals;
      if (key === 'teachingCategories' || key === 'teaching_categories') {
        const canonicalKey = constructQueryParamName(key, scope);
        const altKey =
          key === 'teachingCategories' ? 'pub_teaching_categories' : 'pub_teachingCategories';
        if (!normalizedInitVals?.[canonicalKey]) {
          const altInitVals = initialValues([altKey], liveEdit);
          if (altInitVals?.[altKey]) {
            normalizedInitVals = {
              ...normalizedInitVals,
              [canonicalKey]: altInitVals[altKey],
            };
          }
        }
      }
      if (key === 'dialects' && rawInitVals) {
        const qParamName = constructQueryParamName(key, scope);
        const raw = rawInitVals[qParamName];
        if (raw) {
          const prefixMatch = String(raw).match(/^(has_any|has_all):/);
          const prefix = prefixMatch ? prefixMatch[0] : '';
          const parsed = parseSelectFilterOptions(raw);
          const canonicalSet = new Set();
          parsed.forEach(val => {
            const cd = getCountryDialect(val);
            if (cd) {
              canonicalSet.add(cd.countryCode);
            } else {
              canonicalSet.add(val);
            }
          });
          normalizedInitVals = {
            ...rawInitVals,
            [qParamName]: `${prefix}${Array.from(canonicalSet).join(',')}`,
          };
        }
      }

      return (
        <SelectMultipleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={normalizedInitVals}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <IntegerRangeFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
